import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { FaFacebookF, FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa";
import { toast } from "sonner";
import { z } from "zod";

import { newsletter } from "@/api/newsletter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const newsForm = z.object({
  email: z
    .string()
    .min(1, "E-mail é obrigatório!")
    .email("Digite um e-mail válido"),
});

type NewsForm = z.infer<typeof newsForm>;

export function Newsletter() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<NewsForm>({
    resolver: zodResolver(newsForm),
  });

  const { mutateAsync: newsletterFn } = useMutation({
    mutationFn: newsletter,
    onSuccess: () => {
      toast.success(
        "😄 Sua inscrição foi realizada com sucesso, fique de olho na sua caixa de e-mail!",
      );
      reset();
    },
    onError: () => {
      toast.error("🙁 Erro, tente novamente mais tarde!");
      reset();
    },
  });

  async function handleNewsletter(data: NewsForm) {
    const newsletterData = {
      email: data.email,
    };

    await newsletterFn({ data: newsletterData });
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-purple-950 px-4">
      <div className="w-full max-w-xl text-center text-white">
        <h1 className="mb-4 font-roboto text-xl font-bold text-white">
          Quer receber novidades sobre a Cabelegria ?{" "}
          <br className="max-[480px]:hidden" /> Assine nossa Newsletter:
        </h1>
        <form
          onSubmit={handleSubmit(handleNewsletter)}
          className="mb-4 flex justify-center gap-4"
        >
          <div className="flex w-full flex-col">
            <Input
              id="email"
              type="email"
              placeholder="E-mail"
              {...register("email")}
              autoComplete="off"
              className={
                errors.email?.message
                  ? "focus:ring-red border-[1px] border-red-600 bg-transparent font-roboto font-medium text-red-600 outline-none placeholder:font-roboto placeholder:text-red-600 focus:ring-red-600 focus-visible:outline-none focus-visible:ring-red-600"
                  : "focus:ring-red border-[1px] border-solid bg-transparent font-roboto font-medium text-white outline-none placeholder:font-roboto placeholder:text-white focus:ring-purple-950 focus-visible:outline-none focus-visible:ring-purple-950"
              }
            />
            {errors.email?.message && (
              <p className="pt-1 text-left text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            variant="transparent"
            className="w-3/12 font-roboto text-sm font-medium text-white hover:bg-white hover:text-purple-950"
          >
            {isSubmitting ? "Enviando..." : "Enviar"}
          </Button>
        </form>
      </div>
      <h2 className="pb-4 font-roboto text-xl font-bold text-white">
        Nos encontre nas redes sociais
      </h2>
      <div className="flex gap-4 max-[1024px]:justify-center">
        <a
          href="https://www.instagram.com/cabelegria/"
          title="Instagram Cabelegria"
          target="_blank"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white"
          rel="noreferrer"
        >
          <FaInstagram color="#482682" />
        </a>
        <a
          href="https://www.tiktok.com/@cabelegria?_t=8lp9enKiNgz&_r=1"
          title="Tiktok Cabelegria"
          target="_blank"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white"
          rel="noreferrer"
        >
          <FaTiktok color="#482682" />
        </a>
        <a
          href="https://www.youtube.com/ongcabelegria"
          title="Youtube Cabelegria"
          target="_blank"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white"
          rel="noreferrer"
        >
          <FaYoutube color="#482682" />
        </a>
        <a
          href="https://www.facebook.com/cabelegria"
          title="Facebook Cabelegria"
          target="_blank"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white"
          rel="noreferrer"
        >
          <FaFacebookF color="#482682" />
        </a>
      </div>
    </div>
  );
}
